import { useEffect, useMemo, useState } from "react";
import { pickFirstNonEmptyImageField } from "../../../Utils/StayUpToDate/RenderComponent";
import type { CreatorData } from "./types";
import { syncTemplateComponentCardImageIfNeeded } from "./templateComponentCardImageSync";

export interface UseAboutMoreFromCardImagesResult {
  items: CreatorData["about"]["values"];
  /** True while fetching first feed tiles and writing `template_components.card_image`. */
  isSyncing: boolean;
}

/**
 * Resolves More From hero images from `template_components.card_image`, running
 * the same first-feed → handleFirstImage sync loaders use (website-template only).
 */
export function useAboutMoreFromCardImages(
  items: CreatorData["about"]["values"],
  userId: string,
  username: string,
  enabled: boolean,
): UseAboutMoreFromCardImagesResult {
  const [imageByRowId, setImageByRowId] = useState<Record<string, string>>({});
  const [settledKey, setSettledKey] = useState<string | null>(null);

  const rowIds = useMemo(
    () =>
      items
        .map((item) => item.stayComponentRowId?.trim())
        .filter((id): id is string => Boolean(id)),
    [items],
  );

  const syncKey = rowIds.join("|");
  const needsSync =
    enabled && Boolean(userId.trim()) && Boolean(username.trim()) && rowIds.length > 0;
  // True on the first render that needs a sync, not only after the effect
  // starts — otherwise the stored card image paints, then the feed image swaps in.
  const isSyncing = needsSync && settledKey !== syncKey;

  useEffect(() => {
    if (!needsSync) return;

    let cancelled = false;

    (async () => {
      await Promise.all(
        rowIds.map(async (rowId) => {
          if (cancelled) return;
          try {
            const url = await syncTemplateComponentCardImageIfNeeded(
              userId,
              username,
              rowId,
            );
            if (!cancelled && url) {
              setImageByRowId((prev) => ({ ...prev, [rowId]: url }));
            }
          } catch {
            // non-blocking — card falls back to text-only tile
          }
        }),
      );
      if (!cancelled) setSettledKey(syncKey);
    })();

    return () => {
      cancelled = true;
    };
  }, [needsSync, userId, username, syncKey, rowIds]);

  const itemsWithImages = useMemo(
    () =>
      items.map((item) => {
        const rowId = item.stayComponentRowId?.trim();
        const synced = rowId ? imageByRowId[rowId] : undefined;
        const image =
          synced ||
          pickFirstNonEmptyImageField(item.image) ||
          item.image ||
          "";
        if (!synced && image === item.image) return item;
        return { ...item, image };
      }),
    [items, imageByRowId],
  );

  return { items: itemsWithImages, isSyncing };
}
